// TheBurntPeanut Simulator 9000 — Audio Engine
// Procedural audio using Web Audio API: dark ambient dungeon music + SFX

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let musicGain: GainNode | null = null;
let sfxGain: GainNode | null = null;
// 🥜 Dedicated LOUD bus for Peanut voice lines, parallel to sfxGain → master, so
// his voice doesn't fight the gunfire on the same bus. We duck sfxGain under it.
let voiceGain: GainNode | null = null;
// BETA 22.36 — pro-mix chain (researched): a master safety LIMITER (no clipping
// when voice+sfx+music stack), a COMPRESSOR on the voice bus (consistent, present
// dialogue), and a peaking EQ that CARVES the 2.5 kHz vocal band out of the SFX
// while a voice plays so the Peanut cuts through spectrally, not just by volume.
let masterLimiter: DynamicsCompressorNode | null = null;
let voiceComp: DynamicsCompressorNode | null = null;
let sfxVocalCarve: BiquadFilterNode | null = null;
let musicPlaying = false;
let musicNodes: AudioNode[] = [];

// BETA 15.85 — Sample-backed BGM layer (Dylan's track files) as a PLAYLIST.
// Tracks auto-advance: when one ends, the next plays. After the last track,
// loops back to the first. Each track has a user-facing display name shown
// in the Sound Inspector.
//
// Preload strategy: track 0 (lobby first-track) preloads on Home mount so
// playback is instant on first user click. Track 1+ preload lazily — fetched
// while track 0 is playing so transitions are smooth without front-loading
// 100+ MB of audio.
interface BgmTrack {
  /** Internal filename under /sounds/ */
  url: string;
  /** User-facing label shown in Sound Inspector. */
  displayName: string;
  /** Decoded buffer — populated lazily by loadTrack() */
  buffer: AudioBuffer | null;
  /** BETA 19.29 — In-flight load promise. While set, parallel loadTrack
   *  calls for THIS track await it instead of firing their own fetch +
   *  decode. Cleared on completion (success leaves `buffer` set so the
   *  promise check at the top of loadTrack short-circuits anyway) or on
   *  error (so a future retry isn't permanently locked). */
  _loadPromise?: Promise<void> | null;
  /** BETA 22.52 — track loops forever once reached (no advance). Used by the
   *  menu's instrumental loop bed so the full song never restarts from the
   *  top intro (Dylan: "when the song is over a couple track repeat"). */
  loopForever?: boolean;
  /** Per-track gain multiplier (1 = full). Lets the menu song sit under the
   *  UI sounds + ambience without touching the user's music slider. */
  gain?: number;
}

// BETA 15.92 — Per Dylan: "the songs i gave you are main menu songs, once
// the game loads in and we are actually in game i need it two switch to
// game music which is using the same rules, where audio stops when you do
// shrines and chest and level up. this is in game music. the other music
// you have is all lobby home screen music. fix it please. put this song
// on repeat in game." Two playlists now: LOBBY_PLAYLIST plays on the
// home/menu screens; GAME_PLAYLIST plays once the player enters an actual
// run. Pause/resume/duck logic is unchanged — both contexts share the
// same playback machinery via getActivePlaylist().
const LOBBY_PLAYLIST: BgmTrack[] = [
  // BETA 22.49 — Dylan: "remove all start-screen peanut songs, its overlapping."
  // The lobby now plays ONLY the Sorry Boss theme (the same track as the boot
  // intro), so there's one continuous menu track and nothing double-stacks with
  // the intro. The old roast songs (I Play Video Games / The Anthem / My Friends
  // Are Trash / bungulators_mute_mom) were peanut/streamer content anyway.
  // BETA 22.52 — Menu Feel: the full song plays ONCE at a level that leaves
  // room for the UI kit + world ambience (Dylan: "balanced so you can still
  // hear the UI"), then hands off to an instrumental LOOP BED mixed from his
  // stems (drums+bass+percussion+synth+low guitar, no vocals, −6dB) that
  // repeats forever — the vocal intro never restarts on you.
  { url: '/intro/sorry_boss_music.mp3', displayName: 'Sorry Boss',                buffer: null, gain: 0.78 },
  { url: '/intro/sorry_boss_loop.mp3',  displayName: 'Sorry Boss (instrumental)', buffer: null, loopForever: true },
];

// 23.42 — IN-GAME MUSIC DELETED (Dylan: "start menu music only. i want the
// audio to be atmospheric and intentional"). Empty playlist = entering the
// game STOPS the lobby track (ensureBgmTrackMatchesContext) and
// playCurrentTrack's empty-guard keeps the run silent. Menu music untouched.
const GAME_PLAYLIST: BgmTrack[] = [];

// Active context — determines which playlist getActivePlaylist() returns.
// React routes set this via setBgmContext() on game-mount / game-unmount.
type BgmContext = 'lobby' | 'game';
let _bgmContext: BgmContext = 'lobby';

function getActivePlaylist(): BgmTrack[] {
  return _bgmContext === 'game' ? GAME_PLAYLIST : LOBBY_PLAYLIST;
}

// BETA 16.23 — Per Dylan: "main home screen start menu music sounds like
// it has the blanket effect on it. sounds low. start menu music shouldnt
// be connected to this. there should be two separate music sections in
// code. one for main menu. and one in game. so we dont cross pulute code
// on accident."
//
// Root cause of the bug: prior to 16.23, lobby and game music both routed
// through a SINGLE bgmGain → bgmFilter chain. When the BETA 16.21 death
// replay (or shrine/chest/level-up duck) called startMuffledDuck() and
// then the run ended before endMuffledDuck() could fully restore the
// chain, the lowpass filter + ducked volume STAYED on bgmGain/bgmFilter
// across the run-end → lobby transition. Lobby music inherited the
// muffled state.
//
// Fix: TWO COMPLETELY SEPARATE chains.
//   LOBBY: lobbySource → lobbyBgmGain → masterGain  (no filter, never ducked)
//   GAME:  gameSource  → gameBgmGain  → gameBgmFilter → masterGain
//
// Duck functions ONLY touch gameBgmGain + gameBgmFilter. Lobby chain is
// architecturally insulated — even if a stuck duck slipped through, it
// could never reach lobby music.
//
// `bgmGain` / `bgmFilter` are kept as backwards-compat aliases that point
// to the GAME chain (since every existing duck/muffle caller is a game
// event). Treat them as "game BGM nodes" going forward.
let lobbyBgmGain: GainNode | null = null;
// BETA 22.42 — lobby chain now has its own lowpass (mirrors the game chain) so
// MENU music can be muffled too (e.g. the Junk Drawer "OPEN PC" tab). At 22000 Hz
// it's a transparent bypass; ramp the cutoff down to muffle. See startLobbyMuffle.
let lobbyBgmFilter: BiquadFilterNode | null = null;
let gameBgmGain: GainNode | null = null;
let gameBgmFilter: BiquadFilterNode | null = null;
// Aliases for backwards-compat with code that referenced these names. They
// point to the GAME chain. Lobby code paths must NOT use these.
let bgmGain: GainNode | null = null;
let bgmFilter: BiquadFilterNode | null = null;
let bgmSource: AudioBufferSourceNode | null = null;
let bgmPlaylistIndex = 0;
let bgmManuallyStopped = false;

// BETA — Music slowdown on low HP. Ramp the BGM buffer source's playbackRate
// (which also lowers PITCH — the "everything slows down as you're dying" feel).
// Stored so a track change mid-slowdown re-applies the rate to the new source.
let _bgmDesiredRate = 1.0;
export function setBgmPlaybackRate(rate: number, rampSec = 0.5): void {
  _bgmDesiredRate = rate;
  if (!audioCtx || !bgmSource) return;
  const now = audioCtx.currentTime;
  try {
    const pr = bgmSource.playbackRate;
    pr.cancelScheduledValues(now);
    pr.setValueAtTime(pr.value, now);
    pr.linearRampToValueAtTime(rate, now + Math.max(0.01, rampSec));
  } catch { /* node may be ending — the next source picks up _bgmDesiredRate */ }
}
let bgmLoading = false;
// BETA 16.03 — Single in-flight guard for playCurrentTrack to prevent
// two BufferSources being created concurrently when multiple paths
// (setBgmContext / nextBgmTrack / resumeBgm / onended-advance) race.
// Set true at function entry, reset in finally.
let _trackStartInFlight = false;
const _bgmDefaultVolume = 0.34; // BETA 22.35 — Dylan: bring in-game music down ~15% (0.40 → 0.34)
// BETA 15.97 — Per Dylan: "should be at max volume no matter the music
// volume setting set in game. this only effects in home screen not in
// game. so 2 different music slider." Lobby BGM volume is independent
// of the in-game music-volume setting. The home-screen widget gets its
// own slider; the in-game settings panel slider only affects in-game
// music. Persisted separately to localStorage.
let _lobbyVolume = 1.0;     // home-screen / menu music — defaults max
let _gameVolume  = _bgmDefaultVolume;  // in-game music — tracks the music slider
// _bgmIntendedVolume is the CURRENTLY ACTIVE intended volume (lobby OR
// game depending on context). Kept for backwards compat with duck logic.
// BETA 15.87 — Tracks the user's intended BGM volume so ducking can restore
// to it (instead of guessing or hard-coding). setBgmVolume updates this;
// duckBgm uses it to compute the duck depth + the restore target.
let _bgmIntendedVolume = _bgmDefaultVolume;
function _getActiveContextVolume(): number {
  return _bgmContext === 'game' ? _gameVolume : _lobbyVolume;
}
// BETA 15.88 — True pause/resume support. When pauseBgm() is called we save
// the playback position (in seconds within the current track buffer) and
// stop the source. resumeBgm() creates a new source and starts it at the
// saved offset so playback continues from exactly where it was paused.
let bgmTrackStartCtxTime = 0; // audioCtx.currentTime when current source was last (re)started, adjusted for offset
let bgmPauseOffsetSec = 0;     // seconds into the buffer where playback should resume from
let bgmIsPaused = false;

// BETA 13.84 — bot-farm mute. When bot-farm dashboard launches game iframes
// with `?muted=true` or `?botfarm=true`, this flag is set at module load before
// initAudio fires. masterGain initializes at 0 so the 10 parallel iframes don't
// blast 10× SFX over your Zoom call. setMuted(false) unmutes one tile when you
// click its 🔊 icon for debugging.
let _forceMuted = false;
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  if (params.get('muted') === 'true' || params.get('botfarm') === 'true') {
    _forceMuted = true;
  }
}

/** Toggle audio mute at runtime (bot-farm dashboard per-tile unmute icon). */
export function setMuted(muted: boolean) {
  _forceMuted = muted;
  if (masterGain) masterGain.gain.value = muted ? 0 : 0.7;
}

/** True if audio is currently force-muted (URL param or runtime toggle). */
export function isMuted(): boolean {
  return _forceMuted;
}

// Initialize audio context (must be called from user gesture)
export function initAudio() {
  if (audioCtx) return;
  audioCtx = new AudioContext();

  masterGain = audioCtx.createGain();
  // BETA 13.84: honor URL-param mute (set above at module load) so bot-farm iframes start silent.
  masterGain.gain.value = _forceMuted ? 0 : 0.7;
  // BETA 22.36 — master bus LIMITER between masterGain and the speakers: catches
  // peaks so the mix never clips/distorts when the loud voice + lots of gunfire +
  // music all stack. threshold -6 / ratio 12 / fast 3ms attack = transparent brick-wall.
  masterLimiter = audioCtx.createDynamicsCompressor();
  masterLimiter.threshold.value = -6;
  masterLimiter.knee.value = 6;
  masterLimiter.ratio.value = 12;
  masterLimiter.attack.value = 0.003;
  masterLimiter.release.value = 0.25;
  masterGain.connect(masterLimiter);
  masterLimiter.connect(audioCtx.destination);

  musicGain = audioCtx.createGain();
  musicGain.gain.value = 0.35;
  musicGain.connect(masterGain);

  // SFX bus → vocal-carve EQ → master. The peaking filter rests flat (0 dB) and
  // dips ~5 dB at 2.5 kHz only while a voice plays (see duckSfxForVoice), scooping
  // the speech-presence band out of the gunfire so dialogue reads clearly.
  sfxGain = audioCtx.createGain();
  sfxGain.gain.value = 0.6;
  sfxVocalCarve = audioCtx.createBiquadFilter();
  sfxVocalCarve.type = 'peaking';
  sfxVocalCarve.frequency.value = 2500;
  sfxVocalCarve.Q.value = 1.0;
  sfxVocalCarve.gain.value = 0;
  sfxGain.connect(sfxVocalCarve);
  sfxVocalCarve.connect(masterGain);

  // 🥜 Peanut voice bus — louder than sfx, then a gentle COMPRESSOR (4:1) so every
  // line sits at a consistent, present level (quiet + shouted lines both cut through).
  voiceGain = audioCtx.createGain();
  voiceGain.gain.value = 1.6;
  voiceComp = audioCtx.createDynamicsCompressor();
  voiceComp.threshold.value = -22;
  voiceComp.knee.value = 6;
  voiceComp.ratio.value = 4;
  voiceComp.attack.value = 0.005;
  voiceComp.release.value = 0.18;
  voiceGain.connect(voiceComp);
  voiceComp.connect(masterGain);

  // BETA 15.81 — BGM gain node (Dylan's sample tracks). Lower default than
  // SFX so weapons/hits/shrine sounds naturally cut through the music
  // without needing per-event ducking.
  // BETA 16.23 — Split into TWO separate chains. See comment block above
  // the lobbyBgmGain / gameBgmGain declarations for the why + chain layout.

  // ── LOBBY chain: gain → lowpass filter → master. ──
  // BETA 22.42 — was gain → master (no filter). Added a lowpass (bypassed at
  // 22000 Hz) so menu music can be muffled on demand (Junk Drawer OPEN PC tab)
  // without affecting normal playback. Volume duck + muffle = startLobbyMuffle().
  lobbyBgmGain = audioCtx.createGain();
  lobbyBgmGain.gain.value = _lobbyVolume;
  lobbyBgmFilter = audioCtx.createBiquadFilter();
  lobbyBgmFilter.type = 'lowpass';
  lobbyBgmFilter.frequency.value = 22000; // transparent bypass at start
  lobbyBgmFilter.Q.value = 0.7;
  lobbyBgmGain.connect(lobbyBgmFilter);
  lobbyBgmFilter.connect(masterGain);

  // ── GAME chain: gain → lowpass filter → master. ──
  gameBgmGain = audioCtx.createGain();
  gameBgmGain.gain.value = _gameVolume;

  // BETA 16.12 — BGM lowpass filter for "blanket over the speaker" effect.
  // Per Dylan: "main game audio playing in game ducks heavy and sounds
  // like you put a blanked over the speaker. still playin and not stopped
  // or paused. just under." Routes game BGM through a lowpass: at
  // frequency 22000 Hz it's effectively bypassed (no filtering); on
  // muffle, ramp the cutoff down to ~700 Hz for the muffled sound.
  gameBgmFilter = audioCtx.createBiquadFilter();
  gameBgmFilter.type = 'lowpass';
  gameBgmFilter.frequency.value = 22000; // bypass at start
  gameBgmFilter.Q.value = 0.7;
  gameBgmGain.connect(gameBgmFilter);
  gameBgmFilter.connect(masterGain);

  // Aliases for legacy code paths — point to GAME chain (since every
  // historical duck/muffle caller is a game event).
  bgmGain = gameBgmGain;
  bgmFilter = gameBgmFilter;
}

// Resume audio context if suspended (browser autoplay policy)
export function resumeAudio() {
  if (audioCtx?.state === 'suspended') {
    audioCtx.resume();
  }
}

// ─── Dark Ambient Dungeon Music ─────────────────────────────────────
// Layered drone with low oscillators, filtered noise, and subtle pulses

export function startMusic() {
  if (!audioCtx || !musicGain || musicPlaying) return;
  musicPlaying = true;
  
  // Layer 1: Deep bass drone (C1 = 32.7 Hz)
  const bassDrone = audioCtx.createOscillator();
  bassDrone.type = 'sawtooth';
  bassDrone.frequency.value = 32.7;
  const bassFilter = audioCtx.createBiquadFilter();
  bassFilter.type = 'lowpass';
  bassFilter.frequency.value = 80;
  bassFilter.Q.value = 2;
  const bassGain = audioCtx.createGain();
  bassGain.gain.value = 0.4;
  bassDrone.connect(bassFilter);
  bassFilter.connect(bassGain);
  bassGain.connect(musicGain);
  bassDrone.start();
  musicNodes.push(bassDrone);
  
  // Layer 2: Mid drone with slow LFO modulation (E2 = 82.4 Hz)
  const midDrone = audioCtx.createOscillator();
  midDrone.type = 'sine';
  midDrone.frequency.value = 82.4;
  const midLfo = audioCtx.createOscillator();
  midLfo.type = 'sine';
  midLfo.frequency.value = 0.15; // Very slow wobble
  const midLfoGain = audioCtx.createGain();
  midLfoGain.gain.value = 3; // ±3 Hz wobble
  midLfo.connect(midLfoGain);
  midLfoGain.connect(midDrone.frequency);
  midLfo.start();
  const midGain = audioCtx.createGain();
  midGain.gain.value = 0.15;
  midDrone.connect(midGain);
  midGain.connect(musicGain);
  midDrone.start();
  musicNodes.push(midDrone, midLfo);
  
  // Layer 3: Filtered noise (wind/atmosphere)
  const noiseBuffer = audioCtx.createBuffer(1, audioCtx.sampleRate * 4, audioCtx.sampleRate);
  const noiseData = noiseBuffer.getChannelData(0);
  for (let i = 0; i < noiseData.length; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * 0.5;
  }
  const noiseSource = audioCtx.createBufferSource();
  noiseSource.buffer = noiseBuffer;
  noiseSource.loop = true;
  const noiseFilter = audioCtx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 200;
  noiseFilter.Q.value = 0.5;
  // Slowly sweep the filter
  const noiseLfo = audioCtx.createOscillator();
  noiseLfo.type = 'sine';
  noiseLfo.frequency.value = 0.05;
  const noiseLfoGain = audioCtx.createGain();
  noiseLfoGain.gain.value = 100;
  noiseLfo.connect(noiseLfoGain);
  noiseLfoGain.connect(noiseFilter.frequency);
  noiseLfo.start();
  const noiseGainNode = audioCtx.createGain();
  noiseGainNode.gain.value = 0.12;
  noiseSource.connect(noiseFilter);
  noiseFilter.connect(noiseGainNode);
  noiseGainNode.connect(musicGain);
  noiseSource.start();
  musicNodes.push(noiseSource, noiseLfo);
  
  // Layer 4: Occasional metallic ping (random interval)
  schedulePing();
  
  // Layer 5: Sub-bass pulse (heartbeat-like)
  const pulseOsc = audioCtx.createOscillator();
  pulseOsc.type = 'sine';
  pulseOsc.frequency.value = 24; // Very low sub-bass
  const pulseGain = audioCtx.createGain();
  pulseGain.gain.value = 0;
  // Pulse LFO
  const pulseLfo = audioCtx.createOscillator();
  pulseLfo.type = 'sine';
  pulseLfo.frequency.value = 0.4; // ~24 bpm
  const pulseLfoGain = audioCtx.createGain();
  pulseLfoGain.gain.value = 0.2;
  pulseLfo.connect(pulseLfoGain);
  pulseLfoGain.connect(pulseGain.gain);
  pulseLfo.start();
  pulseOsc.connect(pulseGain);
  pulseGain.connect(musicGain);
  pulseOsc.start();
  musicNodes.push(pulseOsc, pulseLfo);
}

function schedulePing() {
  if (!audioCtx || !musicGain || !musicPlaying) return;
  
  const delay = 3 + Math.random() * 8; // 3-11 seconds
  setTimeout(() => {
    if (!audioCtx || !musicGain || !musicPlaying) return;
    
    // Metallic ping
    const osc = audioCtx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 800 + Math.random() * 1200;
    const pingGain = audioCtx.createGain();
    pingGain.gain.setValueAtTime(0.08, audioCtx.currentTime);
    pingGain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 1.5);
    
    // Add reverb-like delay
    const delay = audioCtx.createDelay();
    delay.delayTime.value = 0.15;
    const feedback = audioCtx.createGain();
    feedback.gain.value = 0.3;
    
    osc.connect(pingGain);
    pingGain.connect(musicGain);
    pingGain.connect(delay);
    delay.connect(feedback);
    feedback.connect(delay);
    feedback.connect(musicGain);
    
    osc.start();
    osc.stop(audioCtx.currentTime + 2);
    
    schedulePing();
  }, delay * 1000);
}

export function stopMusic() {
  musicPlaying = false;
  musicNodes.forEach(node => {
    try {
      if (node instanceof OscillatorNode || node instanceof AudioBufferSourceNode) {
        node.stop();
      }
      node.disconnect();
    } catch (_) { /* already stopped */ }
  });
  musicNodes = [];
}

// ─── Sound Effects ──────────────────────────────────────────────────

// CRT boot beep (title screen)
export function playCRTBoot() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  
  // Three ascending beeps
  [440, 660, 880].forEach((freq, i) => {
    const osc = audioCtx!.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const gain = audioCtx!.createGain();
    gain.gain.setValueAtTime(0, now + i * 0.12);
    gain.gain.linearRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.1);
    osc.connect(gain);
    gain.connect(sfxGain!);
    osc.start(now + i * 0.12);
    osc.stop(now + i * 0.12 + 0.15);
  });
}

// UI click/select beep
export function playUIClick() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  
  const osc = audioCtx.createOscillator();
  osc.type = 'square';
  osc.frequency.value = 1200;
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
  osc.connect(gain);
  gain.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 0.08);
}

// UI hover beep (softer)
export function playUIHover() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  
  const osc = audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 800;
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.05, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
  osc.connect(gain);
  gain.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 0.06);
}

// Deploy/start game sound
export function playDeploy() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  
  // Descending sweep
  const osc = audioCtx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(2000, now);
  osc.frequency.exponentialRampToValueAtTime(100, now + 0.4);
  const filter = audioCtx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 3000;
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.2, now);
  gain.gain.linearRampToValueAtTime(0.15, now + 0.1);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 0.6);
}

// Goop collection sound (mechanical gear crunch + sparkle)
export function playGoopCollect() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  
  // Metallic crunch (noise burst)
  const noiseBuffer = audioCtx.createBuffer(1, audioCtx.sampleRate * 0.15, audioCtx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (audioCtx.sampleRate * 0.04));
  }
  const noise = audioCtx.createBufferSource();
  noise.buffer = noiseBuffer;
  const noiseFilter = audioCtx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 2000;
  noiseFilter.Q.value = 2;
  const noiseGain = audioCtx.createGain();
  noiseGain.gain.value = 0.25;
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(sfxGain);
  noise.start(now);
  
  // Sparkle (ascending arpeggio)
  [880, 1320, 1760, 2200].forEach((freq, i) => {
    const osc = audioCtx!.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = audioCtx!.createGain();
    g.gain.setValueAtTime(0, now + i * 0.05);
    g.gain.linearRampToValueAtTime(0.1, now + i * 0.05 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.2);
    osc.connect(g);
    g.connect(sfxGain!);
    osc.start(now + i * 0.05);
    osc.stop(now + i * 0.05 + 0.25);
  });
}

// Portal activation sound
export function playPortalActivate() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  
  // Rising sweep
  const osc = audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(100, now);
  osc.frequency.exponentialRampToValueAtTime(800, now + 0.8);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.15, now);
  gain.gain.linearRampToValueAtTime(0.2, now + 0.4);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
  osc.connect(gain);
  gain.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 1.2);
  
  // Harmonic shimmer
  const osc2 = audioCtx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(200, now);
  osc2.frequency.exponentialRampToValueAtTime(1600, now + 0.8);
  const gain2 = audioCtx.createGain();
  gain2.gain.setValueAtTime(0.08, now);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
  osc2.connect(gain2);
  gain2.connect(sfxGain);
  osc2.start(now);
  osc2.stop(now + 1.2);
}

// Win/victory fanfare
export function playVictory() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  
  // Triumphant ascending notes
  const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
  notes.forEach((freq, i) => {
    const osc = audioCtx!.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const g = audioCtx!.createGain();
    const t = now + i * 0.15;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.12, t + 0.03);
    g.gain.setValueAtTime(0.12, t + 0.1);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.connect(g);
    g.connect(sfxGain!);
    osc.start(t);
    osc.stop(t + 0.5);
  });
}

// Footstep sound (subtle)
let lastStepTime = 0;
export function playFootstep() {
  if (!audioCtx || !sfxGain) return;
  const now = audioCtx.currentTime;
  if (now - lastStepTime < 0.25) return; // Rate limit
  lastStepTime = now;
  
  const osc = audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 60 + Math.random() * 20;
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.06, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  
  // Add a tiny click
  const noiseBuffer = audioCtx.createBuffer(1, audioCtx.sampleRate * 0.02, audioCtx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (audioCtx.sampleRate * 0.005));
  }
  const noise = audioCtx.createBufferSource();
  noise.buffer = noiseBuffer;
  const nGain = audioCtx.createGain();
  nGain.gain.value = 0.04;
  
  osc.connect(gain);
  gain.connect(sfxGain);
  noise.connect(nGain);
  nGain.connect(sfxGain);
  osc.start(now);
  osc.stop(now + 0.1);
  noise.start(now);
}

// Volume controls
// BETA 20.63 — every setter now honors _forceMuted (URL ?muted=true / ?botfarm=true).
// Saved lobby/menu volume settings used to clobber the mute by calling these with
// 0.7 on mount; force-mute now WINS absolutely so bot-farm + headless test sessions
// stay dead silent no matter what the menu re-applies.
export function setMasterVolume(v: number) {
  const target = _forceMuted ? 0 : Math.max(0, Math.min(1, v));
  if (masterGain) masterGain.gain.value = target;
  // BETA 15.87 — diagnostic so user can verify volume is reaching the engine.
  // Open browser console to see "audio:setMasterVolume 0.5" etc. when sliding.
  console.log('[audio] setMasterVolume', target, 'gain=', masterGain?.gain.value);
}

export function setMusicVolume(v: number) {
  const target = _forceMuted ? 0 : Math.max(0, Math.min(1, v));
  if (musicGain) musicGain.gain.value = target;
  console.log('[audio] setMusicVolume', target, 'gain=', musicGain?.gain.value);
}

export function setSFXVolume(v: number) {
  const target = _forceMuted ? 0 : Math.max(0, Math.min(1, v));
  if (sfxGain) sfxGain.gain.value = target;
  console.log('[audio] setSFXVolume', target, 'gain=', sfxGain?.gain.value);
}

/**
 * BETA 20.63 — absolute kill-switch. Force-mutes + zeroes every gain node and
 * suspends the context. Used by the dev/test harness (window.__forceMuteAudio)
 * to guarantee silence on an already-running session without a reload.
 */
export function forceMuteAll(): void {
  _forceMuted = true;
  if (masterGain) masterGain.gain.value = 0;
  if (musicGain) musicGain.gain.value = 0;
  if (sfxGain) sfxGain.gain.value = 0;
  if (lobbyBgmGain) lobbyBgmGain.gain.value = 0;
  if (gameBgmGain) gameBgmGain.gain.value = 0;
  if (bgmGain) bgmGain.gain.value = 0;
  try { audioCtx?.suspend(); } catch { /* noop */ }
}

/**
 * BETA 15.87 — Diagnostic helper. Returns a snapshot of every gain node's
 * current value + audio context state. Call from console to verify the
 * volume chain is actually applying:
 *   window.__getAudioState()  →  { master: 0.7, music: 0.35, sfx: 0.6, bgm: 0.4, ctx: 'running' }
 */
export function getAudioState(): {
  master: number | null;
  music: number | null;
  sfx: number | null;
  bgm: number | null;
  bgmIntended: number;
  ctx: string;
  forceMuted: boolean;
} {
  return {
    master: masterGain?.gain.value ?? null,
    music: musicGain?.gain.value ?? null,
    sfx: sfxGain?.gain.value ?? null,
    bgm: bgmGain?.gain.value ?? null,
    bgmIntended: _bgmIntendedVolume,
    ctx: audioCtx?.state ?? 'no-context',
    forceMuted: _forceMuted,
  };
}
if (typeof window !== 'undefined') {
  (window as unknown as { __getAudioState: () => unknown }).__getAudioState = getAudioState;
  (window as unknown as { __forceMuteAudio: () => void }).__forceMuteAudio = forceMuteAll;
}

export function isMusicPlaying() {
  return musicPlaying;
}

// ═══════════════════════════════════════════════════════════════
//  BETA 15.81 — BGM (Background Music) — Dylan-provided tracks
// ═══════════════════════════════════════════════════════════════
//
// Independent of the procedural musicGain (drone) so we can swap between
// modes without fighting each other. Auto-starts on first user gesture
// (browser autoplay policy), loops continuously, persists across all game
// phase changes. SFX naturally cut through via volume balance — no per-
// event ducking for v1.

/**
 * Load one playlist track's audio into a decoded AudioBuffer (no playback).
 * Idempotent — early-returns if the buffer is already cached on the track.
 * Safe to call before any user gesture (decode works on a suspended ctx).
 *
 * BETA 19.29 — In-flight Promise dedup added (mirrors the 19.8 fix on
 * `_loadDeathEulogy`). Without it, parallel loadTrack calls for the SAME
 * track could fire simultaneously — common scenario: playCurrentTrack
 * for track N kicks off `void loadTrack(playlist[N+1])` to preload,
 * and 5s later when track N ends + playCurrentTrack runs for N+1, its
 * own `await loadTrack(track)` could race the preload if the preload
 * hasn't completed yet. Browser cache makes the second fetch cheap but
 * decodeAudioData is still redundant work. The promise field on each
 * track makes the second caller await the first's in-flight load.
 */
async function loadTrack(track: BgmTrack): Promise<void> {
  if (!audioCtx) return;
  if (track.buffer) return;
  if (track._loadPromise) return track._loadPromise;
  track._loadPromise = (async () => {
    try {
      const res = await fetch(track.url);
      const arr = await res.arrayBuffer();
      track.buffer = await audioCtx!.decodeAudioData(arr);
    } catch (err) {
      console.warn('[BGM] failed to load', track.url, err);
      // Drop the promise reference on failure so a future retry can
      // start fresh instead of being permanently locked to a rejected
      // promise that resolves with no buffer.
      track._loadPromise = null;
    }
  })();
  return track._loadPromise;
}

/**
 * BETA 15.85 — Preload the FIRST playlist track ahead of any user gesture
 * so playback is instant on first click. Other tracks load lazily as the
 * playlist advances (via the kick-off in playCurrentTrack).
 */
export async function preloadBgm(): Promise<void> {
  if (getActivePlaylist().length === 0) return;
  await loadTrack(getActivePlaylist()[0]);
}

/**
 * Play the playlist track at bgmPlaylistIndex. When it finishes, advances
 * to the next track and recurses. After the last track, loops back to
 * index 0. Only fires advance on natural end (not when stopBgm() is called).
 */
async function playCurrentTrack(): Promise<void> {
  if (!audioCtx || !bgmGain) return;
  if (bgmSource) return; // already playing — caller bug
  if (getActivePlaylist().length === 0) return;
  // BETA 16.03 — IN-FLIGHT GUARD. Per Dylan: "i had two sons playing at
  // once... same son playing twice milleseconds aparts and then i hit
  // next son and a song kept playing and the second song started both
  // playing at the same time." Root cause: playCurrentTrack is async.
  // The `if (bgmSource) return` guard at the top doesn't protect against
  // re-entry between bgmSource being null (after stop) and being set to
  // the new source (later in this function). Two callers (e.g. setBgmContext
  // + nextBgmTrack + resumeBgm + onended) racing past the early return
  // would each create their own buffer source. Now: a single in-flight
  // promise gates re-entry. If a track-start is already in flight, return
  // the existing promise instead of creating another.
  if (_trackStartInFlight) return;
  _trackStartInFlight = true;
  try {
    const track = getActivePlaylist()[bgmPlaylistIndex];
    if (!track.buffer) {
      if (bgmLoading) return;
      bgmLoading = true;
      try {
        await loadTrack(track);
      } finally {
        bgmLoading = false;
      }
    }
    if (!track.buffer) return; // load failed — silent
    if (bgmManuallyStopped) return; // user stopped while we were loading
    if (bgmSource) return; // some OTHER path won the race — bail

    const src = audioCtx.createBufferSource();
    src.buffer = track.buffer;
    src.playbackRate.value = _bgmDesiredRate;  // BETA — carry low-HP slowdown across track changes
    // BETA 22.52 — loopForever tracks (the menu's instrumental bed) repeat
    // in place instead of advancing; tiny edge margins dodge encoder padding
    // clicks at the seam. Everything else advances via onended as before.
    if (track.loopForever) {
      src.loop = true;
      src.loopStart = 0.1;
      src.loopEnd = Math.max(1, track.buffer.duration - 0.1);
    } else {
      src.loop = false;  // single-track playback; advance via onended
    }
    // BETA 16.23 — Route to lobby OR game chain based on active context.
    // Lobby chain has NO filter (never gets the muffled "blanket" effect)
    // and its gain is independent so a stuck game-chain duck can never
    // bleed into menu music.
    const destGain = _bgmContext === 'lobby' ? lobbyBgmGain : gameBgmGain;
    if (!destGain) return; // initAudio hasn't run yet
    // BETA 22.52 — per-track trim (menu song sits under the UI layer).
    if (track.gain != null && track.gain !== 1) {
      const trim = audioCtx.createGain();
      trim.gain.value = track.gain;
      src.connect(trim);
      trim.connect(destGain);
    } else {
      src.connect(destGain);
    }
    src.onended = () => {
      if (bgmSource !== src) return; // stale ended (stopBgm cleared us)
      bgmSource = null;
      if (bgmManuallyStopped) return;
      bgmPauseOffsetSec = 0;  // natural advance starts the next track from 0
      bgmPlaylistIndex = (bgmPlaylistIndex + 1) % getActivePlaylist().length;
      void playCurrentTrack();
    };
    bgmSource = src;
    // BETA 15.88 — start at saved pause offset so resumeBgm continues from
    // where pauseBgm left off. For fresh starts / track jumps, callers reset
    // bgmPauseOffsetSec to 0 first.
    const offset = bgmPauseOffsetSec;
    bgmTrackStartCtxTime = audioCtx.currentTime - offset;
    src.start(audioCtx.currentTime, offset);

    // Eagerly preload the NEXT track so transition is smooth (no fetch+decode
    // delay between songs). Fires in parallel; current track keeps playing.
    const nextIdx = (bgmPlaylistIndex + 1) % getActivePlaylist().length;
    if (nextIdx !== bgmPlaylistIndex) {
      void loadTrack(getActivePlaylist()[nextIdx]);
    }
  } finally {
    _trackStartInFlight = false;
  }
}

/**
 * BETA 15.85 — Start BGM playback at the current playlist position. The
 * playlist auto-advances to the next track when each finishes. Idempotent
 * if already playing. Default playlist starts at index 0 ("I Play Video
 * Games") and runs through all tracks before looping back.
 */
export async function startBgm(): Promise<void> {
  if (!audioCtx || !bgmGain) return;
  bgmManuallyStopped = false;
  bgmIsPaused = false;
  bgmPauseOffsetSec = 0;  // fresh start — play from beginning
  if (bgmSource) return; // already playing
  await playCurrentTrack();
}

/** Stop BGM playback. Buffers stay loaded for fast restart. Sets the
 *  manually-stopped flag so the source's onended handler doesn't auto-
 *  advance to the next track. */
export function stopBgm(): void {
  bgmManuallyStopped = true;
  bgmIsPaused = false;
  bgmPauseOffsetSec = 0;
  if (bgmSource) {
    try { bgmSource.stop(); } catch { /* already stopped */ }
    bgmSource = null;
  }
}

/**
 * Manual track-skip: jump to the next track in the playlist. If the user
 * wants to skip the current song.
 */
export async function nextBgmTrack(): Promise<void> {
  bgmPlaylistIndex = (bgmPlaylistIndex + 1) % getActivePlaylist().length;
  bgmPauseOffsetSec = 0;  // new track starts from 0
  bgmIsPaused = false;
  if (bgmSource) {
    try { bgmSource.stop(); } catch { /* */ }
    bgmSource = null;
  }
  bgmManuallyStopped = false;
  await playCurrentTrack();
}

/**
 * Jump to a specific track by URL. If found in playlist, sets index +
 * plays it. If not in the playlist, no-op. (Use for direct track swaps.)
 */
export async function playBgmTrack(url: string): Promise<void> {
  const idx = getActivePlaylist().findIndex(t => t.url === url);
  if (idx < 0) return;
  bgmPlaylistIndex = idx;
  bgmPauseOffsetSec = 0;  // new track starts from 0
  bgmIsPaused = false;
  if (bgmSource) {
    try { bgmSource.stop(); } catch { /* */ }
    bgmSource = null;
  }
  bgmManuallyStopped = false;
  await playCurrentTrack();
}

/**
 * BETA 15.88 — TRUE pause: save current playback position + stop the source.
 * Different from stopBgm() which forgets the position. Resume with
 * resumeBgm() to continue from exactly where you paused.
 *
 * Per Dylan: "I dont want the ducking to start on a shrine unless i
 * unlock it fully. and it should be a pause in the music not a ducking."
 * Used for shrine activation completion (full unlock) so the buff
 * selection moment is silent and focused on the shrine sound.
 */
export function pauseBgm(): void {
  if (!bgmSource || !audioCtx || bgmIsPaused) return;
  bgmIsPaused = true;
  bgmManuallyStopped = true;  // prevent onended auto-advance
  // Compute current position within the buffer (where playback was at
  // the moment of pause). Clamp to buffer duration so we don't read past
  // the end if the track was about to finish.
  const elapsed = audioCtx.currentTime - bgmTrackStartCtxTime;
  bgmPauseOffsetSec = Math.max(0, elapsed);
  if (bgmSource.buffer) {
    bgmPauseOffsetSec = Math.min(bgmPauseOffsetSec, bgmSource.buffer.duration);
  }
  try { bgmSource.stop(); } catch { /* already stopped */ }
  bgmSource = null;
}

/**
 * BETA 15.88 — Resume from pauseBgm(). Creates a new BufferSource at the
 * saved playback offset so the music continues from exactly where it
 * paused. No-op if not currently paused.
 */
export async function resumeBgm(): Promise<void> {
  if (!bgmIsPaused) return;
  bgmIsPaused = false;
  bgmManuallyStopped = false;
  // playCurrentTrack reads bgmPauseOffsetSec to start the new source at
  // the saved position. After it kicks off, the offset stays set (further
  // pauses re-compute from bgmTrackStartCtxTime); only natural track end
  // or a manual track jump resets it to 0.
  await playCurrentTrack();
}

export function isBgmPaused(): boolean {
  return bgmIsPaused;
}

export function setBgmVolume(v: number): void {
  if (!gameBgmGain || !audioCtx) return;
  const target = Math.max(0, Math.min(1, v));
  // BETA 15.97 — This slider is the IN-GAME music volume. Stored separately
  // from the lobby volume so the home screen has its own control.
  // BETA 16.23 — Always ramps gameBgmGain (the game chain). Lobby chain is
  // independent.
  _gameVolume = target;
  if (_bgmContext === 'game') {
    _bgmIntendedVolume = target;
  }
  const now = audioCtx.currentTime;
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  gameBgmGain.gain.linearRampToValueAtTime(target, now + 0.3);
  console.log('[audio] setBgmVolume (game)', target);
}

/**
 * BETA 15.97 — Set the LOBBY music volume independently. Drives the
 * home-screen widget's volume slider. Persisted via localStorage by the
 * widget so it survives reloads.
 *
 * BETA 16.23 — Always ramps lobbyBgmGain (the lobby chain). Game chain
 * is independent — a stuck game-chain duck cannot affect lobby music.
 */
export function setLobbyVolume(v: number): void {
  if (!lobbyBgmGain || !audioCtx) return;
  const target = Math.max(0, Math.min(1, v));
  _lobbyVolume = target;
  if (_bgmContext === 'lobby') {
    _bgmIntendedVolume = target;
  }
  const now = audioCtx.currentTime;
  lobbyBgmGain.gain.cancelScheduledValues(now);
  lobbyBgmGain.gain.setValueAtTime(lobbyBgmGain.gain.value, now);
  lobbyBgmGain.gain.linearRampToValueAtTime(target, now + 0.3);
  console.log('[audio] setLobbyVolume', target);
}

export function getLobbyVolume(): number { return _lobbyVolume; }
export function getGameVolume(): number { return _gameVolume; }

/**
 * BETA 15.87 — Audio ducking. Per Dylan: "when i do shrines and chest the
 * main music in the lobby doesnt pause and you cant hear the sounds of the
 * chest and such. there needs to be a smart way to do this." Temporarily
 * lowers the BGM volume so a key SFX (shrine activate, chest open, level
 * up) can be heard clearly, then ramps the BGM back up to the user's
 * intended level.
 *
 *   durationSec    — total time the duck is held (200ms ramp-down + hold +
 *                    500ms ramp-up). Default 2.5s.
 *   depthMultiplier — duck level as a fraction of intended volume.
 *                    Default 0.3 = "30% of intended" (e.g., if user set
 *                    music to 0.6, duck drops to 0.18).
 *
 * Multiple overlapping calls: each call cancels prior schedule and reschedules
 * from current value. The latest duckBgm() wins. Acceptable for v1.
 */
export function duckBgm(durationSec: number = 2.5, depthMultiplier: number = 0.3): void {
  // BETA 16.23 — Always operates on gameBgmGain. Lobby chain stays clean.
  // Restore target uses _gameVolume directly (not the shared
  // _bgmIntendedVolume) so a duck called during a context flip can't
  // restore game gain to a stale lobby volume.
  if (!gameBgmGain || !audioCtx) return;
  const now = audioCtx.currentTime;
  const restoreTarget = _gameVolume;
  const duckedVolume = restoreTarget * Math.max(0, Math.min(1, depthMultiplier));
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  gameBgmGain.gain.linearRampToValueAtTime(duckedVolume, now + 0.2);
  gameBgmGain.gain.setValueAtTime(duckedVolume, now + durationSec);
  gameBgmGain.gain.linearRampToValueAtTime(restoreTarget, now + durationSec + 0.5);
}

/**
 * BETA 15.92 — Sustained ducking, paired start/end. Different from duckBgm()
 * which auto-restores after a fixed duration. Use this for state-based ducks
 * that last "as long as some condition is true" — e.g., low-HP heartbeat:
 * BGM ducks HARD while player is low-HP, restores when they heal back up.
 *
 * Per Dylan: "music ducks hard when low health and the heartbeat low health
 * is amplified. until you are no longer in the low health and the music
 * goes back up fading up rather quickly. in sync."
 *
 *   depthMultiplier — fraction of intended volume to hold at while sustained.
 *                     Default 0.15 = duck to 15% (very quiet, heartbeat carries).
 */
export function startSustainedDuck(depthMultiplier: number = 0.15): void {
  if (!gameBgmGain || !audioCtx) return;
  const now = audioCtx.currentTime;
  const restoreTarget = _gameVolume;
  const duckedVolume = restoreTarget * Math.max(0, Math.min(1, depthMultiplier));
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  gameBgmGain.gain.linearRampToValueAtTime(duckedVolume, now + 0.25);
}

export function endSustainedDuck(): void {
  if (!gameBgmGain || !audioCtx) return;
  const now = audioCtx.currentTime;
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  // Quick fade-up — Dylan: "fading up rather quickly. in sync."
  gameBgmGain.gain.linearRampToValueAtTime(_gameVolume, now + 0.4);
}

// ── BETA 22.42 — LOBBY (menu) muffled duck ────────────────────────────────────
// Same "blanket over the speaker" as startMuffledDuck, but for the LOBBY chain
// (home/menu music), which the game-chain duck can't reach. Used by the Junk
// Drawer "OPEN PC" tab so the menu music lowers + muffles under the case-crack.
// Guarded against mute so it can never un-mute the music. Held until endLobbyMuffle().
export function startLobbyMuffle(depthMultiplier: number = 0.35): void {
  if (!lobbyBgmGain || !audioCtx || !lobbyBgmFilter || isMuted()) return;
  const now = audioCtx.currentTime;
  const ducked = _lobbyVolume * Math.max(0, Math.min(1, depthMultiplier));
  lobbyBgmGain.gain.cancelScheduledValues(now);
  lobbyBgmGain.gain.setValueAtTime(lobbyBgmGain.gain.value, now);
  lobbyBgmGain.gain.linearRampToValueAtTime(ducked, now + 0.2);
  lobbyBgmFilter.frequency.cancelScheduledValues(now);
  lobbyBgmFilter.frequency.setValueAtTime(lobbyBgmFilter.frequency.value, now);
  lobbyBgmFilter.frequency.linearRampToValueAtTime(500, now + 0.2); // muffled
}
export function endLobbyMuffle(): void {
  if (!lobbyBgmGain || !audioCtx || !lobbyBgmFilter || isMuted()) return;
  const now = audioCtx.currentTime;
  lobbyBgmGain.gain.cancelScheduledValues(now);
  lobbyBgmGain.gain.setValueAtTime(lobbyBgmGain.gain.value, now);
  lobbyBgmGain.gain.linearRampToValueAtTime(_lobbyVolume, now + 0.4);
  lobbyBgmFilter.frequency.cancelScheduledValues(now);
  lobbyBgmFilter.frequency.setValueAtTime(lobbyBgmFilter.frequency.value, now);
  lobbyBgmFilter.frequency.linearRampToValueAtTime(22000, now + 0.4); // bypass
}

/**
 * BETA 16.12 — Muffled duck: heavy volume duck + lowpass filter for the
 * "blanket over the speaker" effect. Per Dylan: "main game audio playing
 * in game ducks heavy and sounds like you put a blanked over the speaker.
 * still playin and not stopped or paused. just under. is this possible
 * to do for when shrines are fully activated, chest opens, and level
 * ups? do the ducking the entire time those screens are up?"
 *
 * Combines two effects:
 *   - Volume duck to depth × intendedVolume (default 0.4 = 40%)
 *   - Lowpass cutoff drop to 500 Hz (rolls off all highs/mids — muffled)
 *
 * Ramps in over 200 ms. Held until endMuffledDuck() is called. Idempotent
 * — calling twice in a row just refreshes the schedule.
 *
 * Usage: shrine activate / chest open / level-up open → startMuffledDuck();
 * shrine pick / chest pick / level-up pick / skip → endMuffledDuck();
 */
export function startMuffledDuck(depthMultiplier: number = 0.55): void {
  // BETA 16.23 — Always operates on gameBgmGain + gameBgmFilter (the GAME
  // chain). Even if a stuck duck slips through cleanup, lobby chain is
  // architecturally insulated and cannot be muffled. Restore target uses
  // _gameVolume directly.
  if (!gameBgmGain || !audioCtx || !gameBgmFilter) return;
  const now = audioCtx.currentTime;
  const duckedVolume = _gameVolume * Math.max(0, Math.min(1, depthMultiplier));
  // BETA 16.18 — Per Dylan: "10 percent less on [the muffel]. but rais the
  // volume up slightly during the duckings." Volume depth 0.4 → 0.55,
  // cutoff 500 → 700 Hz (~10% less muffled).
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  gameBgmGain.gain.linearRampToValueAtTime(duckedVolume, now + 0.2);
  gameBgmFilter.frequency.cancelScheduledValues(now);
  gameBgmFilter.frequency.setValueAtTime(gameBgmFilter.frequency.value, now);
  gameBgmFilter.frequency.exponentialRampToValueAtTime(700, now + 0.2);
}

export function endMuffledDuck(): void {
  if (!gameBgmGain || !audioCtx || !gameBgmFilter) return;
  const now = audioCtx.currentTime;
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  gameBgmGain.gain.linearRampToValueAtTime(_gameVolume, now + 0.4);
  gameBgmFilter.frequency.cancelScheduledValues(now);
  gameBgmFilter.frequency.setValueAtTime(gameBgmFilter.frequency.value, now);
  gameBgmFilter.frequency.exponentialRampToValueAtTime(22000, now + 0.4);
}

/**
 * BETA 16.23 — Hard-reset the GAME chain to clean state (full volume +
 * unmuffled filter). Belt-and-suspenders cleanup for context transitions:
 * called by setBgmContext when switching INTO lobby so any stuck duck
 * from the run that just ended is wiped before lobby music starts. Also
 * safe to call defensively on initGame/run-start.
 *
 * IMPORTANT: this only touches the GAME chain. Lobby chain isn't part
 * of this reset — it's already insulated by the split architecture.
 */
export function resetGameBgmChain(): void {
  if (!gameBgmGain || !audioCtx || !gameBgmFilter) return;
  const now = audioCtx.currentTime;
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(_gameVolume, now);
  gameBgmFilter.frequency.cancelScheduledValues(now);
  gameBgmFilter.frequency.setValueAtTime(22000, now);
}

/**
 * BETA 16.14 — Timed muffled duck. Same blanket-over-speaker effect as
 * startMuffledDuck() but auto-restores after `durationSec`. Used for
 * one-shot events that don't have a UI screen blocking gameplay (e.g.
 * Chick Magnet activation — sound plays, duck briefly, then back to
 * normal). Per Dylan: "audio ducks for chick magents sound when
 * activated. there is just no pop us screen. just ducks when the audio
 * plays for activating chick magnet."
 *
 * Schedule:
 *   0.00s  → start ramps (volume + filter cutoff DOWN)
 *   0.20s  → fully ducked + muffled
 *   durationSec  → start restore ramps
 *   durationSec + 0.40s → fully restored
 */
export function duckBgmMuffled(durationSec: number = 2.5, depthMultiplier: number = 0.55): void {
  // BETA 16.23 — Always operates on gameBgmGain + gameBgmFilter. Lobby
  // chain stays clean.
  if (!gameBgmGain || !audioCtx || !gameBgmFilter) return;
  const now = audioCtx.currentTime;
  const restoreTarget = _gameVolume;
  const duckedVolume = restoreTarget * Math.max(0, Math.min(1, depthMultiplier));
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  gameBgmGain.gain.linearRampToValueAtTime(duckedVolume, now + 0.2);
  gameBgmGain.gain.setValueAtTime(duckedVolume, now + durationSec);
  gameBgmGain.gain.linearRampToValueAtTime(restoreTarget, now + durationSec + 0.4);
  gameBgmFilter.frequency.cancelScheduledValues(now);
  gameBgmFilter.frequency.setValueAtTime(gameBgmFilter.frequency.value, now);
  gameBgmFilter.frequency.exponentialRampToValueAtTime(700, now + 0.2);
  gameBgmFilter.frequency.setValueAtTime(700, now + durationSec);
  gameBgmFilter.frequency.exponentialRampToValueAtTime(22000, now + durationSec + 0.4);
}

/** 🥜 The dedicated voice bus (for Peanut lines). Null until initAudio runs. */
export function getVoiceGain(): GainNode | null { return voiceGain; }

/**
 * 🥜 Duck the SFX bus (weapons/hits) under a Peanut voice line, then restore —
 * so his voice cuts through the gunfire. Mirrors duckBgmMuffled's ramp shape.
 *   depthMultiplier 0.4 = sfx drops to 40% while he talks.
 */
export function duckSfxForVoice(durationSec: number = 2.0, depthMultiplier: number = 0.4): void {
  if (!sfxGain || !audioCtx) return;
  const now = audioCtx.currentTime;
  const full = 0.6; // sfxGain's resting value
  const ducked = full * Math.max(0, Math.min(1, depthMultiplier));
  // BETA 22.36 — exponential envelope duck (researched): FAST attack (timeConstant
  // 0.08 ≈ 250 ms to settle), HOLD, then SLOW release (0.30 ≈ 900 ms). Smooth +
  // natural, none of the "robotic pumping" a hard linear ramp gives.
  sfxGain.gain.cancelScheduledValues(now);
  sfxGain.gain.setValueAtTime(sfxGain.gain.value, now);
  sfxGain.gain.setTargetAtTime(ducked, now, 0.08);
  sfxGain.gain.setTargetAtTime(full, now + durationSec, 0.30);
  // Carve the 2.5 kHz speech-presence band out of the SFX while he talks so the
  // voice cuts through spectrally, then restore flat.
  if (sfxVocalCarve) {
    sfxVocalCarve.gain.cancelScheduledValues(now);
    sfxVocalCarve.gain.setValueAtTime(sfxVocalCarve.gain.value, now);
    sfxVocalCarve.gain.setTargetAtTime(-8, now, 0.08); // BETA 22.37 — deeper carve (was -5)
    sfxVocalCarve.gain.setTargetAtTime(0, now + durationSec, 0.30);
  }
}

/**
 * 🥜 BETA 22.37 — GENTLE BGM dip under a voice (Dylan: the old big dip + muffle was
 * distracting). Just a tiny gain nudge (~2%), NO lowpass muffle — the EQ carve on the
 * SFX bus + the loud voice bus do the cut-through, so the music barely moves.
 */
export function duckBgmForVoice(durationSec: number = 2.0, depthMultiplier: number = 0.98): void {
  if (!gameBgmGain || !audioCtx) return;
  const now = audioCtx.currentTime;
  const rest = _gameVolume;
  const ducked = rest * Math.max(0, Math.min(1, depthMultiplier));
  gameBgmGain.gain.cancelScheduledValues(now);
  gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
  gameBgmGain.gain.setTargetAtTime(ducked, now, 0.08);
  gameBgmGain.gain.setTargetAtTime(rest, now + durationSec, 0.30);
}

/**
 * BETA 15.92 — Switch BGM context between lobby (home/menu) and game (in-run).
 * Per Dylan: "the songs i gave you are main menu songs, once the game loads
 * in and we are actually in game i need it two switch to game music."
 *
 * - Resets playlist index + pause offset (the new playlist starts fresh from
 *   track 0; the previous context's pause position is forgotten on switch)
 * - If BGM was playing, restarts with the new playlist's first track
 * - Idempotent: calling with the current context is a no-op
 *
 * Wired in GameCanvas.tsx via useEffect — mount sets 'game', unmount sets
 * 'lobby' so going back to the menu auto-resumes lobby music.
 */
export async function setBgmContext(ctx: BgmContext): Promise<void> {
  if (_bgmContext === ctx) return;
  _bgmContext = ctx;
  bgmPlaylistIndex = 0;
  bgmPauseOffsetSec = 0;
  bgmIsPaused = false;
  _bgmIntendedVolume = _getActiveContextVolume();
  // BETA 16.23 — Belt-and-suspenders. When switching INTO lobby, hard-
  // reset the GAME chain (full volume + unmuffled filter) so any duck
  // that was active when the run ended is wiped. The split architecture
  // means this is no longer strictly required to keep lobby clean
  // (lobby chain is independent), but it leaves the game chain in a
  // sane state for the next run. When switching INTO game, refresh the
  // game gain to the user's intended game volume so a previous run's
  // mid-duck state can't leak in (defense in depth).
  if (audioCtx) {
    if (ctx === 'lobby') {
      resetGameBgmChain();
    }
    if (gameBgmGain && ctx === 'game') {
      const now = audioCtx.currentTime;
      gameBgmGain.gain.cancelScheduledValues(now);
      gameBgmGain.gain.setValueAtTime(gameBgmGain.gain.value, now);
      gameBgmGain.gain.linearRampToValueAtTime(_gameVolume, now + 0.25);
    }
    // Always refresh lobby gain to its intended volume — defensive ramp
    // so a previously-set transient lobby gain (e.g. from a slider)
    // settles to the canonical _lobbyVolume.
    if (lobbyBgmGain) {
      const now = audioCtx.currentTime;
      lobbyBgmGain.gain.cancelScheduledValues(now);
      lobbyBgmGain.gain.setValueAtTime(lobbyBgmGain.gain.value, now);
      lobbyBgmGain.gain.linearRampToValueAtTime(_lobbyVolume, now + 0.25);
    }
  }
  // BETA 15.96 — Per Dylan: "where did my home screen music go?" Bug:
  // when the player clicked QUIT on the death screen, GameCanvas
  // unmounted → setBgmContext('lobby') fired, but the previous logic
  // only restarted music if `bgmSource` was non-null. After a death,
  // bgmSource is null (stopped by playDeathEulogy) — so the lobby
  // playlist never started, leaving the home screen silent.
  // Now: always stop whatever's running (BGM source AND any active
  // death eulogy) and start the new playlist if AudioContext is ready.
  if (_deathEulogySource) {
    console.warn('[deathEulogy] stopped EARLY by setBgmContext()', new Error().stack?.split('\n').slice(0, 5).join('\n'));
    try { _deathEulogySource.stop(); } catch { /* */ }
    _deathEulogySource = null;
  }
  if (bgmSource) {
    try { bgmSource.stop(); } catch { /* */ }
    bgmSource = null;
  }
  bgmManuallyStopped = false;
  // Only auto-start if AudioContext is running (i.e. user has already
  // interacted). On first page load, BGM starts from the first user
  // gesture — that path goes through startBgm() / playCurrentTrack()
  // which will pick up the current _bgmContext anyway.
  if (audioCtx && audioCtx.state === 'running') {
    await playCurrentTrack();
  }
}

export function getBgmContext(): BgmContext {
  return _bgmContext;
}

export function isBgmPlaying(): boolean {
  return bgmSource !== null;
}

/** Returns the URL of the currently-playing track, or null if stopped. */
export function getBgmCurrentTrackUrl(): string | null {
  if (!bgmSource) return null;
  return getActivePlaylist()[bgmPlaylistIndex]?.url ?? null;
}

// BETA 18.15 — Dylan: "what happened to my game music when in a game. the
// main menu music is playing." 18.8 defensive resync only fired when
// _bgmContext didn't match desired. But if context flag is correctly
// 'game' AND the playing source is somehow from LOBBY_PLAYLIST, the
// resync did nothing (early-return at line 1061 of setBgmContext). Now:
// explicit "ensure track matches context" helper. Compares current track
// URL against the expected playlist; if mismatch, hard-restarts the track.
export async function ensureBgmTrackMatchesContext(): Promise<void> {
  if (!audioCtx || !bgmSource) return;
  const expectedPlaylist = _bgmContext === 'game' ? GAME_PLAYLIST : LOBBY_PLAYLIST;
  const currentTrackUrl = getActivePlaylist()[bgmPlaylistIndex]?.url ?? null;
  const trackInExpectedPlaylist = expectedPlaylist.some(t => t.url === currentTrackUrl);
  if (trackInExpectedPlaylist) return; // all good
  // Mismatch — stop current source + restart from playlist[0] in the
  // correct context. Note: setBgmContext won't help here because
  // _bgmContext already matches; we need to force-restart playback.
  try { bgmSource.stop(); } catch { /* ignore */ }
  bgmSource = null;
  bgmPlaylistIndex = 0;
  bgmPauseOffsetSec = 0;
  await playCurrentTrack();
}

/** Returns the display name of the currently-playing OR currently-paused
 *  track. BETA 16.03 — was: returns null when bgmSource is null. That hid
 *  the BgmControls widget the moment user clicked pause (per Dylan: "when
 *  i clicked pause it disapeared"). Now: returns the track at the active
 *  playlist index whenever the BGM has been started at least once this
 *  session, regardless of pause state. Returns null only if BGM has truly
 *  never started (audio not yet initialized). */
export function getBgmCurrentTrackName(): string | null {
  // If audio hasn't been initialized at all, return null (no track yet).
  if (!audioCtx || !bgmGain) return null;
  // If BGM has never been started this session (no source has ever been
  // created and we're not paused), return null. Otherwise return the
  // track name — even when paused or briefly between tracks.
  if (!bgmSource && !bgmIsPaused) return null;
  return getActivePlaylist()[bgmPlaylistIndex]?.displayName ?? null;
}

/** All playlist tracks (for Sound Inspector display). */
export function getBgmPlaylist(): Array<{ url: string; displayName: string }> {
  return getActivePlaylist().map(t => ({ url: t.url, displayName: t.displayName }));
}

// ─── Death eulogy (BETA 15.93) ────────────────────────────────────────
// One-shot audio that overrides BGM during the death screen sequence.
// Per Dylan + PSBOT RUN spec: "BGM stops dead. Eulogy plays at 100%."
// Different from regular BGM: doesn't loop, doesn't pause for SFX
// (shrines/chests don't fire on death screen anyway). Routed through
// the BGM gain node so the music slider still controls its volume.

const DEATH_EULOGY_URL = '/sounds/death_eulogy.mp3';
let _deathEulogyBuffer: AudioBuffer | null = null;
let _deathEulogySource: AudioBufferSourceNode | null = null;
// BETA 19.8 — In-flight promise. Without this, concurrent callers (Home
// mount + GameCanvas mount + MenuRenderer mount, each potentially firing
// 2-3× due to HMR or React tree retention) all race past the "buffer
// already loaded?" check and start their own 26 MB fetch + decode.
// Observed in audit: 8 parallel fetches of /sounds/death_eulogy.mp3 at
// startup — ~200 MB of redundant network + decode work, classic
// heartbeat-freeze candidate on slow devices like the ROG Ally. Caching
// the promise dedupes — every caller awaits the same single load.
let _deathEulogyLoadPromise: Promise<void> | null = null;

async function _loadDeathEulogy(): Promise<void> {
  if (!audioCtx || _deathEulogyBuffer) return;
  // BETA 19.8 — Reuse in-flight load if one is already running.
  if (_deathEulogyLoadPromise) return _deathEulogyLoadPromise;
  _deathEulogyLoadPromise = (async () => {
    try {
      console.log('[deathEulogy] fetch start', DEATH_EULOGY_URL);
      const res = await fetch(DEATH_EULOGY_URL);
      console.log('[deathEulogy] fetch ok, status=', res.status, 'arraybuffer next');
      const arr = await res.arrayBuffer();
      console.log('[deathEulogy] arraybuffer got, size=', arr.byteLength, 'bytes — decoding');
      _deathEulogyBuffer = await audioCtx!.decodeAudioData(arr);
      console.log('[deathEulogy] decode ok, duration=', _deathEulogyBuffer.duration, 'sec');
    } catch (err) {
      console.warn('[audio] failed to load death_eulogy', err);
      // Allow retry on next call after a failure.
      _deathEulogyLoadPromise = null;
    }
  })();
  return _deathEulogyLoadPromise;
}

/**
 * BETA 15.96 — Eagerly preload the death eulogy ahead of any death event.
 * Called from Home.tsx on mount so the 25 MB WAV is fetched + decoded in
 * the background while the player is still in the menu. By the time they
 * die, the buffer is ready and playback is instant. Without this preload,
 * playDeathEulogy() awaits the load synchronously — adds 1-3 seconds of
 * silence between death and music starting (or worse, if fetch fails
 * during the death moment, the eulogy never plays).
 */
export async function preloadDeathEulogy(): Promise<void> {
  await _loadDeathEulogy();
}

/**
 * Start the death eulogy. Stops any in-progress BGM cleanly first so
 * the eulogy plays alone. Called once when state.phase transitions to
 * 'gameover'. Idempotent — calling twice while already playing is a
 * no-op.
 */
export async function playDeathEulogy(): Promise<void> {
  // BETA 15.95 — Diagnostic logs for Dylan's "music did not change" report.
  // If audioCtx/bgmGain isn't ready or fetch/decode fails, we silently
  // bail without telling anyone — these logs surface the failure point.
  console.log('[deathEulogy] play requested. audioCtx=', !!audioCtx, 'bgmGain=', !!bgmGain, 'bgmSource=', !!bgmSource, 'eulogyBuffer=', !!_deathEulogyBuffer);
  if (!audioCtx || !bgmGain) {
    console.warn('[deathEulogy] aborted — audio not initialized');
    return;
  }
  if (_deathEulogySource) {
    console.log('[deathEulogy] already playing, no-op');
    return;
  }
  // Stop BGM (whatever playlist is active). Eulogy carries the moment alone.
  if (bgmSource) {
    try { bgmSource.stop(); } catch { /* */ }
    bgmSource = null;
  }
  bgmManuallyStopped = true;
  bgmIsPaused = false;
  bgmPauseOffsetSec = 0;

  if (!_deathEulogyBuffer) {
    console.log('[deathEulogy] loading buffer from', DEATH_EULOGY_URL);
    await _loadDeathEulogy();
  }
  if (!_deathEulogyBuffer) {
    console.warn('[deathEulogy] aborted — buffer load failed');
    return;
  }

  const src = audioCtx.createBufferSource();
  src.buffer = _deathEulogyBuffer;
  src.loop = false;
  src.connect(bgmGain);
  const _startedAt = Date.now();
  src.onended = () => {
    if (_deathEulogySource === src) _deathEulogySource = null;
    const elapsedSec = (Date.now() - _startedAt) / 1000;
    const expectedSec = _deathEulogyBuffer?.duration ?? 0;
    if (elapsedSec < expectedSec - 1) {
      console.warn('[deathEulogy] onended fired EARLY — elapsed=', elapsedSec.toFixed(2), 'expected=', expectedSec.toFixed(2));
    } else {
      console.log('[deathEulogy] ended naturally — elapsed=', elapsedSec.toFixed(2), 'expected=', expectedSec.toFixed(2));
    }
  };
  _deathEulogySource = src;
  src.start();
  console.log('[deathEulogy] started, duration=', _deathEulogyBuffer.duration, 'sec');
}

/** Stop the eulogy immediately. Called when player clicks REPLAY/QUIT
 *  or otherwise leaves the death screen. BETA 15.96 — also resumes
 *  the active context's BGM (lobby or game) since the eulogy stopped
 *  bgmSource before starting. Without this, REPLAY restart leaves the
 *  player in silence (no in-game music after a death-then-replay).
 *  BETA 16.05 — diagnostic console.log to surface premature stops. */
export function stopDeathEulogy(): void {
  if (_deathEulogySource) {
    console.warn('[deathEulogy] stopped EARLY by stopDeathEulogy()', new Error().stack?.split('\n').slice(0, 5).join('\n'));
    try { _deathEulogySource.stop(); } catch { /* already stopped */ }
    _deathEulogySource = null;
  }
  // Resume the active context's BGM playlist so the player isn't left
  // in silence. Only fires if AudioContext is running and BGM isn't
  // already playing.
  bgmManuallyStopped = false;
  bgmIsPaused = false;
  bgmPauseOffsetSec = 0;
  bgmPlaylistIndex = 0;
  if (audioCtx && audioCtx.state === 'running' && !bgmSource) {
    void playCurrentTrack();
  }
}

export function isDeathEulogyPlaying(): boolean {
  return _deathEulogySource !== null;
}

/** Expose internals for gameSfx module */
export function getAudioContext(): AudioContext | null { return audioCtx; }
export function getSfxGainNode(): GainNode | null { return sfxGain; }

// ═══════════════════════════════════════════════════════════════════════
// BETA 22.52 — MENU FEEL v1 (Audio Director brief). Two pieces:
//  1. playMenuSound(name) — lazy-loaded ElevenLabs UI one-shots → sfx bus
//     (inherits master mute + limiter + the vocal carve, like all sfx).
//     ±3% pitch jitter per play so 200 hovers an hour never fatigue.
//  2. Menu ambience engine — the world outside the menu: three low steady
//     beds (fire / wind / energy hum) looped at DIFFERENT lengths so their
//     phase drift never audibly repeats, plus sparse random one-shots
//     (thunder, distant monster, far boom, metal creak) every 15–45s at
//     random volume + pan. Beds carry continuity; random events kill
//     repetition — that's what makes it listenable for hours.
// ═══════════════════════════════════════════════════════════════════════

const _menuBuf = new Map<string, AudioBuffer>();
const _menuLoading = new Set<string>();

async function _loadMenuSound(name: string): Promise<AudioBuffer | null> {
  const cached = _menuBuf.get(name);
  if (cached) return cached;
  if (!audioCtx || _menuLoading.has(name)) return null;
  _menuLoading.add(name);
  try {
    const res = await fetch(`/sounds/${name}.mp3`);
    const raw = await res.arrayBuffer();
    const buf = await audioCtx.decodeAudioData(raw);
    _menuBuf.set(name, buf);
    return buf;
  } catch {
    return null;
  } finally {
    _menuLoading.delete(name);
  }
}

/** Warm the whole UI kit at menu mount so the FIRST hover already has sound. */
export function preloadMenuSounds(): void {
  ['ui_hover', 'ui_select', 'ui_confirm', 'ui_back', 'ui_denied', 'ui_tab',
    'ui_carousel_tick', 'ui_carousel_stop', 'ui_ready', 'ui_unready',
    'ui_smoke_slam', 'ui_slam_down', 'ui_rip_tear', 'ui_logo_stamp',
    'ui_pack_open', 'ui_pack_close', 'ui_plate_tap', 'ui_plate_slap',
  ].forEach(n => void _loadMenuSound(n));
}

export function playMenuSound(name: string, volume = 0.6, pan = 0): void {
  if (!audioCtx || !sfxGain) return;
  const buf = _menuBuf.get(name);
  if (!buf) { void _loadMenuSound(name); return; }   // warms for next time
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = 1 + (Math.random() - 0.5) * 0.06;  // ±3% anti-fatigue
  const g = audioCtx.createGain();
  g.gain.value = volume;
  const p = audioCtx.createStereoPanner();
  p.pan.value = pan;
  src.connect(g); g.connect(p); p.connect(sfxGain);
  src.start();
}

// ── Menu ambience engine ────────────────────────────────────────────────
let _ambGain: GainNode | null = null;
let _ambSources: AudioBufferSourceNode[] = [];
let _ambTimer: number | null = null;
let _ambRunning = false;

const AMB_BEDS: Array<{ name: string; vol: number }> = [
  { name: 'ui_amb_fire', vol: 0.16 },
  { name: 'ui_amb_wind', vol: 0.10 },
  { name: 'ui_amb_hum',  vol: 0.12 },
];
const AMB_SHOTS = ['ui_amb_thunder', 'ui_amb_monster', 'ui_amb_creak', 'ui_amb_boom'];

export function startMenuAmbience(): void {
  if (_ambRunning || !audioCtx || !masterGain) return;
  _ambRunning = true;
  _ambGain = audioCtx.createGain();
  _ambGain.gain.value = 0;
  _ambGain.connect(masterGain);
  _ambGain.gain.linearRampToValueAtTime(1, audioCtx.currentTime + 2.5); // fade the world in

  AMB_BEDS.forEach((bed, i) => {
    void _loadMenuSound(bed.name).then(buf => {
      if (!buf || !_ambRunning || !audioCtx || !_ambGain) return;
      const src = audioCtx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      // Trim the loop edges + give each bed a slightly different loop length —
      // the three beds phase-drift against each other so the combined texture
      // doesn't perceptibly repeat for tens of minutes.
      src.loopStart = 0.25;
      src.loopEnd = Math.max(1, buf.duration - 0.25 - i * 0.7);
      const g = audioCtx.createGain();
      g.gain.value = bed.vol;
      src.connect(g); g.connect(_ambGain);
      src.start();
      _ambSources.push(src);
    });
  });

  const scheduleShot = () => {
    if (!_ambRunning) return;
    _ambTimer = window.setTimeout(() => {
      if (!_ambRunning || !audioCtx || !_ambGain) return;
      const name = AMB_SHOTS[Math.floor(Math.random() * AMB_SHOTS.length)];
      void _loadMenuSound(name).then(buf => {
        if (!buf || !_ambRunning || !audioCtx || !_ambGain) return;
        const src = audioCtx.createBufferSource();
        src.buffer = buf;
        const g = audioCtx.createGain();
        g.gain.value = 0.05 + Math.random() * 0.08;         // always distant
        const p = audioCtx.createStereoPanner();
        p.pan.value = (Math.random() - 0.5) * 1.4;          // somewhere out there
        src.connect(g); g.connect(p); p.connect(_ambGain);
        src.start();
      });
      scheduleShot();
    }, 15000 + Math.random() * 30000);                       // every 15–45s
  };
  scheduleShot();
}

export function stopMenuAmbience(): void {
  if (!_ambRunning) return;
  _ambRunning = false;
  if (_ambTimer !== null) { window.clearTimeout(_ambTimer); _ambTimer = null; }
  if (audioCtx && _ambGain) {
    _ambGain.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.6);
  }
  const sources = _ambSources; _ambSources = [];
  const gainToKill = _ambGain; _ambGain = null;
  window.setTimeout(() => {
    sources.forEach(s => { try { s.stop(); } catch { /* already stopped */ } });
    try { gainToKill?.disconnect(); } catch { /* fine */ }
  }, 700);
}
